/**
 * The one line before a quote or packet goes out: what this does to the
 * customer's budget (threads/customer-billing-budget/mockup-access.html §2).
 * Informs, never blocks. Renders nothing when no live budget covers the work.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { BudgetRing, useCanSeeMoney } from "./chip";
import { impactSentence, shortCustomerName, type BudgetHealth, type Impact } from "./model";

const BOX: Record<BudgetHealth, string> = {
    good: "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    warn: "border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10",
    over: "border-red-200 bg-red-50/70 dark:border-red-500/30 dark:bg-red-500/10",
};

export function BudgetImpactLine({
    estimateId,
    invoiceId,
    className,
}: {
    estimateId?: string | null;
    invoiceId?: string | null;
    className?: string;
}) {
    const canSeeMoney = useCanSeeMoney();
    const target = estimateId ? `estimateId=${estimateId}` : invoiceId ? `invoiceId=${invoiceId}` : null;
    const enabled = !!target && canSeeMoney;
    const q = useQuery({
        queryKey: ["budget-impact", target],
        queryFn: () => api.get<Impact>(`/api/budgets/impact?${target}`),
        enabled,
        staleTime: 30_000,
        retry: false,
    });
    if (!enabled || !q.data || q.data.lines.length === 0) return null;
    const impact = q.data;
    const who = shortCustomerName(impact.customerName);
    return (
        <div className={cn("space-y-1.5", className)} data-testid="budget-impact">
            {impact.lines.map((line) => {
                const s = impactSentence(impact, line, who);
                return (
                    <div
                        key={line.budgetId}
                        className={cn("flex gap-2 rounded-md border px-3 py-2 text-sm", BOX[s.health])}
                    >
                        <BudgetRing
                            usedPct={line.amountCents > 0 ? (line.afterCents / line.amountCents) * 100 : 0}
                            health={s.health}
                            size={18}
                        />
                        <div className="min-w-0">
                            <div className="font-semibold">{s.text}</div>
                            <div className="text-xs text-muted-foreground">{s.sub}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
